import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { api } from '@/lib/api'

export interface CreatePaymentIntentInput {
  plan: string
  billing_period: 'monthly' | 'yearly'
  /** Si true et l'user n'a pas déjà eu de trial, la sub démarre en mode
   *  trialing (14 jours gratuits). Sinon le backend skip silencieusement
   *  et facture immédiatement — la réponse expose `trial_applied`. */
  is_trial?: boolean
  /** Réceptionniste IA choisie sur le site (pack mensuel), posée dès que
   *  l'abonnement est payant. */
  ai_pack?: string
  /** Identifiant GA4 du visiteur (cookie `_ga`) : relie le premier paiement au clic pub. */
  ga_client_id?: string
}

export interface CreatePaymentIntentResponse {
  /** Null on a trial: the subscription is only created once the card is confirmed. */
  subscription_id: string | null
  /** Trial only: the SetupIntent to confirm, then hand to `startTrial`. */
  setup_intent_id?: string
  client_secret: string
  /** 'setup' en mode trial (capture CB à 0$), 'payment' sinon. */
  intent_type: 'payment' | 'setup'
  amount: number
  currency: string
  plan_key: string
  /** Indique si le trial a été réellement appliqué (false si déjà utilisé). */
  trial_applied: boolean
  /** Timestamp epoch (secondes) de fin du trial. Présent si trial_applied. */
  trial_end: number | null
  /** Montant qui sera facturé après le trial (en cents). Utile pour UI. */
  amount_after_trial: number
}

/** POST /api/stripe/create-payment-intent/ — flow Stripe Elements embedded. */
export async function createPaymentIntent(
  input: CreatePaymentIntentInput,
): Promise<CreatePaymentIntentResponse> {
  const { data } = await api.post<CreatePaymentIntentResponse>(
    '/api/stripe/create-payment-intent/',
    input,
  )
  return data
}

export interface StartTrialResponse {
  subscription_id: string
  status: string
  trial_end: number | null
}

/** POST /api/stripe/start-trial/ — starts the 14-day trial once the card is
 *  confirmed. Idempotent: the Stripe webhook may have started it already. */
export async function startTrial(setupIntentId: string): Promise<StartTrialResponse> {
  const { data } = await api.post<StartTrialResponse>('/api/stripe/start-trial/', {
    setup_intent_id: setupIntentId,
  })
  return data
}

/** The free trial was already used (2 Nov 2026). No trial starts; the account
 *  can still subscribe. The screen never says how the server knows. */
export class TrialAlreadyUsedError extends Error {
  constructor() {
    super('trial_already_used')
    this.name = 'TrialAlreadyUsedError'
  }
}

/** Same call, retried: right after confirmSetup the webhook may be creating the
 *  same subscription. Throws only TrialAlreadyUsedError: otherwise the webhook
 *  is the safety net. */
export async function startTrialWithRetry(setupIntentId: string): Promise<StartTrialResponse | null> {
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      return await startTrial(setupIntentId)
    } catch (err) {
      const status = isAxiosError(err) ? err.response?.status : undefined
      if (isAxiosError(err) && err.response?.data?.code === 'trial_already_used') throw new TrialAlreadyUsedError()
      // 409 subscription_exists: a subscription is already live, nothing to retry.
      if (status === 400 || status === 404 || status === 409) return null
      await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)))
    }
  }
  return null
}

export interface CreateCheckoutSessionInput {
  plan: string
  billing_period: 'monthly' | 'yearly'
  is_trial?: boolean
  locale?: string
  /** URL absolue où Stripe redirige après succès. Ignoré si invalide. */
  success_url?: string
  /** URL absolue où Stripe redirige si l'user annule. Ignoré si invalide. */
  cancel_url?: string
}

export interface CreateCheckoutSessionResponse {
  checkout_url: string
  session_id: string
}

/** POST /api/stripe/create-checkout-session/ — ancien flow (utilisé pour les trials). */
export async function createCheckoutSession(
  input: CreateCheckoutSessionInput,
): Promise<CreateCheckoutSessionResponse> {
  const { data } = await api.post<CreateCheckoutSessionResponse>(
    '/api/stripe/create-checkout-session/',
    input,
  )
  return data
}

/** Hook mutation : lance un checkout Stripe et redirige le navigateur. */
export function useCheckoutRedirect() {
  return useMutation({
    mutationFn: async (input: CreateCheckoutSessionInput) => {
      const data = await createCheckoutSession(input)
      window.location.href = data.checkout_url
      return data
    },
  })
}

export interface CustomerPortalResponse {
  portal_url: string
}

/** POST /api/stripe/create-customer-portal/ — session Stripe Billing Portal. */
export async function createCustomerPortalSession(
  locale: string,
): Promise<CustomerPortalResponse> {
  const { data } = await api.post<CustomerPortalResponse>(
    '/api/stripe/create-customer-portal/',
    { locale },
  )
  return data
}

/** Hook mutation : ouvre le Customer Portal (redirige le navigateur). */
export function useCustomerPortal() {
  return useMutation({
    mutationFn: async (locale: string) => {
      const data = await createCustomerPortalSession(locale)
      window.location.href = data.portal_url
      return data
    },
  })
}

// ============================================================
// Payment history — invoices Stripe
// ============================================================

export interface PaymentHistoryItem {
  id: number
  amount: number
  amount_formatted: string
  currency: string
  status: string
  status_display: string
  description: string
  receipt_url: string
  created_at: string
}

interface PaymentHistoryResponse {
  payments: PaymentHistoryItem[]
}

/** GET /api/stripe/payment-history/ — liste paginée des paiements/factures. */
export function usePaymentHistory() {
  return useQuery({
    queryKey: ['payment-history'],
    queryFn: async () => {
      const { data } = await api.get<PaymentHistoryResponse>(
        '/api/stripe/payment-history/',
      )
      return data.payments
    },
    staleTime: 60 * 1000,
  })
}

// ============================================================
// Payment method — carte par défaut + update via SetupIntent
// ============================================================

export interface PaymentMethodInfo {
  has_payment_method: boolean
  brand?: string
  last4?: string
  exp_month?: number | null
  /** Link : courriel du compte */
  label?: string
  exp_year?: number | null
}

/** GET /api/stripe/payment-method/ — méthode de paiement par défaut.
 *  `enabled` permet de ne déclencher l'appel Stripe que pour le groupe qui en a
 *  besoin (ex: OnboardingGuard, uniquement pour un manager en essai sans numéro). */
export function usePaymentMethod(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['payment-method'],
    queryFn: async () => {
      const { data } = await api.get<PaymentMethodInfo>(
        '/api/stripe/payment-method/',
      )
      return data
    },
    staleTime: 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}

interface CreateSetupIntentResponse {
  client_secret: string
}

/** POST /api/stripe/create-setup-intent/ — SetupIntent pour mettre à jour la carte. */
export function useCreateSetupIntent() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<CreateSetupIntentResponse>(
        '/api/stripe/create-setup-intent/',
      )
      return data
    },
  })
}

// ============================================================
// Change subscription — upgrade/downgrade starter ↔ pro
// ============================================================

export interface PreviewChangeResponse {
  current_plan_key: string | null
  target_plan_key: string
  immediate_total: number // en cents (0 en downgrade ou trial)
  currency: string
  next_payment_attempt: number | null
  /** True si downgrade (Pro → Starter). Le changement prend effet à
   *  effective_at (= fin de période courante), pas de crédit retourné. */
  is_downgrade?: boolean
  /** True si l'user est en essai gratuit. Le nouveau plan s'appliquera
   *  à la fin du trial. Aucune charge ni crédit dans tous les cas. */
  is_trial?: boolean
  /** Timestamp epoch de la date d'effet du changement (trial_end ou
   *  fin de période). */
  effective_at?: number | null
}

/** POST /api/stripe/preview-change-subscription/ — proration immediate. */
export function usePreviewChangeSubscription() {
  return useMutation({
    mutationFn: async (plan: 'starter' | 'pro') => {
      const { data } = await api.post<PreviewChangeResponse>(
        '/api/stripe/preview-change-subscription/',
        { plan },
      )
      return data
    },
  })
}

/** POST /api/stripe/change-subscription/ — applique l'upgrade/downgrade. */
export function useChangeSubscription() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (plan: 'starter' | 'pro') => {
      const { data } = await api.post<{
        message: string
        previous_plan_key: string | null
        new_plan_key: string
      }>('/api/stripe/change-subscription/', { plan })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-status'] })
      queryClient.invalidateQueries({ queryKey: ['payment-history'] })
    },
  })
}

// ============================================================
// Cancel / reactivate subscription
// ============================================================

/** POST /api/stripe/cancel-subscription/ — programme l'annulation en fin de période. */
export function useCancelSubscription() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/api/stripe/cancel-subscription/')
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-status'] })
    },
  })
}

/** POST /api/stripe/reactivate-subscription/ — annule l'annulation programmée. */
export function useReactivateSubscription() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/api/stripe/reactivate-subscription/')
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-status'] })
    },
  })
}

// ============================================================
// Activate subscription now — sortie volontaire d'essai
// ============================================================

export interface TrialActivationPreview {
  amount_total: number // en cents (TTC)
  amount_subtotal: number
  amount_tax: number
  currency: string
  plan: string
  plan_display: string
  has_payment_method: boolean
  payment_method: {
    brand: string
    last4: string
  } | null
}

/** GET /api/stripe/preview-trial-activation/ — montant exact si activation immédiate. */
export function usePreviewTrialActivation(enabled = true) {
  return useQuery({
    queryKey: ['preview-trial-activation'],
    queryFn: async () => {
      const { data } = await api.get<TrialActivationPreview>(
        '/api/stripe/preview-trial-activation/',
      )
      return data
    },
    enabled,
    staleTime: 30 * 1000,
  })
}

export interface ActivateSubscriptionNowSuccess {
  success: true
  message: string
  plan: string
  amount_paid: number | null
  invoice_pdf_url: string | null
  invoice_hosted_url: string | null
}

export interface ActivateSubscriptionNowFailure {
  success: false
  error: string
  status: 'payment_failed' | string
  decline_code?: string | null
}

/** POST /api/stripe/activate-subscription-now/ — termine l'essai et facture aujourd'hui. */
export function useActivateSubscriptionNow() {
  const queryClient = useQueryClient()
  return useMutation<ActivateSubscriptionNowSuccess>({
    mutationFn: async () => {
      const { data } = await api.post<ActivateSubscriptionNowSuccess>(
        '/api/stripe/activate-subscription-now/',
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-status'] })
      queryClient.invalidateQueries({ queryKey: ['payment-history'] })
      queryClient.invalidateQueries({ queryKey: ['trial-usage'] })
      queryClient.invalidateQueries({ queryKey: ['preview-trial-activation'] })
    },
  })
}

/** POST /api/stripe/set-default-payment-method/ — set la PM comme défaut Customer. */
export function useSetDefaultPaymentMethod() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (paymentMethodId: string) => {
      const { data } = await api.post<{ ok: boolean }>(
        '/api/stripe/set-default-payment-method/',
        { payment_method_id: paymentMethodId },
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-method'] })
      queryClient.invalidateQueries({ queryKey: ['subscription-status'] })
    },
  })
}
